import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Bike, Eye, EyeOff } from "lucide-react";
import DriversMap from "@/components/DriversMap";
import { toast } from "sonner";

export function DriversTab({ drivers, reload }) {
  const [driverFormOpen, setDriverFormOpen] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", email: "", password: "", phone: "", vehicle_type: "motorcycle" });
  const [showNewDriverPwd, setShowNewDriverPwd] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null); // null | driver object
  const [showEditDriverPwd, setShowEditDriverPwd] = useState(false);

  const createDriver = async () => {
    if (!newDriver.name || !newDriver.email || newDriver.password.length < 6 || !newDriver.phone) {
      toast.error("أكمل جميع الحقول (كلمة مرور 6 أحرف+)");
      return;
    }
    try {
      await api.post("/admin/drivers", newDriver);
      toast.success("تم إنشاء حساب المندوب");
      setDriverFormOpen(false);
      setNewDriver({ name: "", email: "", password: "", phone: "", vehicle_type: "motorcycle" });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    }
  };

  const removeDriver = async (d) => {
    if (!confirm(`إزالة صلاحيات المندوب ${d.name}؟`)) return;
    try {
      await api.delete(`/admin/drivers/${d.user_id}`);
      toast.success("تمت الإزالة");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    }
  };

  const openEditDriver = (d) => {
    setEditingDriver({
      user_id: d.user_id,
      name: d.name || "",
      email: d.email || "",
      phone: d.phone || "",
      vehicle_type: d.vehicle_type || "motorcycle",
      password: "", // empty means "don't change"
    });
    setShowEditDriverPwd(false);
  };

  const saveEditDriver = async () => {
    if (!editingDriver) return;
    const payload = {
      name: editingDriver.name,
      email: editingDriver.email,
      phone: editingDriver.phone,
      vehicle_type: editingDriver.vehicle_type,
    };
    if (editingDriver.password && editingDriver.password.trim().length > 0) {
      if (editingDriver.password.length < 6) {
        toast.error("كلمة المرور يجب أن تكون 6 أحرف فأكثر");
        return;
      }
      payload.password = editingDriver.password;
    }
    try {
      await api.patch(`/admin/drivers/${editingDriver.user_id}`, payload);
      toast.success("تم حفظ التعديلات");
      setEditingDriver(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    }
  };

  return (
    <>
    <div>
      {/* Live drivers map */}
      <DriversMap drivers={drivers} />

      <div className="mt-4 mb-4 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#6B6B6B]">إدارة مناديب التوصيل</p>
        <Button
          onClick={() => setDriverFormOpen(true)}
          className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white"
          data-testid="admin-add-driver"
        >
          <Plus className="w-4 h-4 me-2" /> إضافة مندوب
        </Button>
      </div>

      {drivers.length === 0 ? (
        <div className="jh-card p-10 text-center text-[#6B6B6B]">
          <Bike className="w-12 h-12 mx-auto text-[#E05D3A]/60" />
          <p className="mt-3">لم يتم إضافة مناديب بعد</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drivers.map((d) => (
            <div key={d.user_id} className="jh-card p-4" data-testid={`admin-driver-${d.user_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#E05D3A] text-white flex items-center justify-center font-bold flex-shrink-0">
                  {d.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-[#1A1A1A] truncate">{d.name}</h3>
                  <p className="text-xs text-[#6B6B6B] truncate">{d.email}</p>
                </div>
                <Badge className={d.driver_status === "available" ? "bg-[#00A85A] text-white" : d.driver_status === "busy" ? "bg-amber-500 text-white" : "bg-gray-300 text-gray-700"}>
                  {d.driver_status === "available" ? "متاح" : d.driver_status === "busy" ? "مشغول" : "غير متصل"}
                </Badge>
              </div>
              <div className="mt-3 text-xs text-[#6B6B6B] space-y-1">
                <p>📱 {d.phone}</p>
                <p>🚗 {d.vehicle_type === "motorcycle" ? "دراجة نارية" : d.vehicle_type === "car" ? "سيارة" : "دراجة"}</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                <div className="bg-[#F5F5F5] rounded-lg p-2">
                  <p className="text-[10px] text-[#6B6B6B]">مكتملة</p>
                  <p className="font-bold text-[#00A85A]">{d.delivered_count || 0}</p>
                </div>
                <div className="bg-[#F5F5F5] rounded-lg p-2">
                  <p className="text-[10px] text-[#6B6B6B]">نشطة</p>
                  <p className="font-bold text-[#E05D3A]">{d.active_count || 0}</p>
                </div>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => openEditDriver(d)}
                className="w-full mt-3 rounded-full text-[#E05D3A] hover:bg-[#FEF0EE] text-xs border-[#FCD9CF]"
                data-testid={`admin-edit-driver-${d.user_id}`}
              >
                <Pencil className="w-3.5 h-3.5 me-1" /> تعديل البيانات
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => removeDriver(d)}
                className="w-full mt-2 rounded-full text-red-600 hover:bg-red-50 text-xs"
                data-testid={`admin-remove-driver-${d.user_id}`}
              >
                <Trash2 className="w-3.5 h-3.5 me-1" /> إزالة صلاحيات
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>

    <Dialog open={driverFormOpen} onOpenChange={setDriverFormOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>إضافة مندوب جديد</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>الاسم</Label><Input value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} data-testid="driver-form-name" /></div>
          <div><Label>البريد</Label><Input type="email" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} data-testid="driver-form-email" /></div>
          <div>
            <Label>كلمة المرور (6+ أحرف)</Label>
            <div className="relative mt-1">
              <Input
                type={showNewDriverPwd ? "text" : "password"}
                value={newDriver.password}
                onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })}
                className="pe-10"
                data-testid="driver-form-password"
              />
              <button type="button" onClick={() => setShowNewDriverPwd(!showNewDriverPwd)} className="absolute inset-y-0 end-3 flex items-center text-[#6B6B6B]" aria-label="toggle">
                {showNewDriverPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div><Label>رقم الجوال</Label><Input value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} placeholder="+9665xxxxxxxx" data-testid="driver-form-phone" /></div>
          <div>
            <Label>نوع المركبة</Label>
            <Select value={newDriver.vehicle_type} onValueChange={(v) => setNewDriver({ ...newDriver, vehicle_type: v })}>
              <SelectTrigger data-testid="driver-form-vehicle"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="motorcycle">🏍️ دراجة نارية</SelectItem>
                <SelectItem value="car">🚗 سيارة</SelectItem>
                <SelectItem value="bike">🚴 دراجة</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDriverFormOpen(false)}>إلغاء</Button>
          <Button onClick={createDriver} className="bg-[#E05D3A] hover:bg-[#C24B2C] text-white" data-testid="driver-form-save">إضافة</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit driver dialog */}
    <Dialog open={!!editingDriver} onOpenChange={(open) => !open && setEditingDriver(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>تعديل بيانات المندوب</DialogTitle>
        </DialogHeader>
        {editingDriver && (
          <div className="space-y-3 py-2">
            <div><Label>الاسم</Label><Input value={editingDriver.name} onChange={(e) => setEditingDriver({ ...editingDriver, name: e.target.value })} data-testid="edit-driver-name" /></div>
            <div><Label>البريد</Label><Input type="email" value={editingDriver.email} onChange={(e) => setEditingDriver({ ...editingDriver, email: e.target.value })} data-testid="edit-driver-email" /></div>
            <div><Label>رقم الجوال</Label><Input value={editingDriver.phone} onChange={(e) => setEditingDriver({ ...editingDriver, phone: e.target.value })} placeholder="+9665xxxxxxxx" data-testid="edit-driver-phone" /></div>
            <div>
              <Label>نوع المركبة</Label>
              <Select value={editingDriver.vehicle_type} onValueChange={(v) => setEditingDriver({ ...editingDriver, vehicle_type: v })}>
                <SelectTrigger data-testid="edit-driver-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="motorcycle">🏍️ دراجة نارية</SelectItem>
                  <SelectItem value="car">🚗 سيارة</SelectItem>
                  <SelectItem value="bike">🚴 دراجة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>كلمة مرور جديدة <span className="text-xs text-[#8B8B8B]">(اتركها فارغة لعدم التغيير)</span></Label>
              <div className="relative mt-1">
                <Input
                  type={showEditDriverPwd ? "text" : "password"}
                  value={editingDriver.password}
                  onChange={(e) => setEditingDriver({ ...editingDriver, password: e.target.value })}
                  placeholder="••••••"
                  className="pe-10"
                  data-testid="edit-driver-password"
                />
                <button type="button" onClick={() => setShowEditDriverPwd(!showEditDriverPwd)} className="absolute inset-y-0 end-3 flex items-center text-[#6B6B6B]" aria-label="toggle">
                  {showEditDriverPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-[11px] text-[#8B8B8B] mt-1">لأسباب أمنية، كلمات المرور الحالية مشفّرة ولا يمكن استرجاعها. اضغط زر العين لعرض كلمة المرور الجديدة أثناء كتابتها.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditingDriver(null)}>إلغاء</Button>
          <Button onClick={saveEditDriver} className="bg-[#E05D3A] hover:bg-[#C24B2C] text-white" data-testid="edit-driver-save">حفظ</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
